import { useEffect, useState } from 'react';

/**
 * US state boundaries for the geo_strategy maps: US Census cartographic
 * boundaries (public domain), simplified and vendored as
 * `web/src/assets/us-states.json` by scripts/build-us-states.mjs. Loaded
 * lazily (own chunk) the first time a state/national map renders.
 */

/** A ring is a flat [lng, lat, lng, lat, ...] list. */
export type FlatRing = number[];

export interface UsState {
  abbr: string;
  /** Polygons; each is an outer ring followed by any holes. */
  polys: FlatRing[][];
}

export interface UsStatesData {
  states: Record<string, UsState>;
  /** Exterior outline of the contiguous US (arcs used by exactly one state). */
  outline: FlatRing[];
}

let pending: Promise<UsStatesData | null> | null = null;
export function loadUsStates(): Promise<UsStatesData | null> {
  pending ??= import('../../assets/us-states.json').then((m) => m.default as unknown as UsStatesData).catch(() => null);
  return pending;
}

export function useUsStates(enabled: boolean): UsStatesData | null {
  const [data, setData] = useState<UsStatesData | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    void loadUsStates().then((d) => {
      if (live) setData(d);
    });
    return () => {
      live = false;
    };
  }, [enabled]);
  return data;
}

/** Resolve "Minnesota", "minnesota" or "MN" to the canonical state name, else null. */
export function resolveStateName(label: string, data: UsStatesData): string | null {
  const key = label.trim().toLowerCase();
  if (!key) return null;
  for (const [name, state] of Object.entries(data.states)) {
    if (name.toLowerCase() === key || state.abbr.toLowerCase() === key) return name;
  }
  return null;
}

export const NON_CONTIGUOUS = new Set(['Alaska', 'Hawaii']);

export interface LatLngBounds {
  sw: { lat: number; lng: number };
  ne: { lat: number; lng: number };
}

/** Continental US, used to fit the national map. */
export const CONTIGUOUS_US_BOUNDS: LatLngBounds = { sw: { lat: 24.4, lng: -125 }, ne: { lat: 49.4, lng: -66.9 } };

export function boundsOfRings(rings: FlatRing[]): LatLngBounds | null {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const ring of rings) {
    for (let i = 0; i + 1 < ring.length; i += 2) {
      const lng = ring[i]!;
      const lat = ring[i + 1]!;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return Number.isFinite(minLat) ? { sw: { lat: minLat, lng: minLng }, ne: { lat: maxLat, lng: maxLng } } : null;
}

/** Every ring (outer + holes) of the named states. */
export function stateRings(data: UsStatesData, names: string[]): FlatRing[] {
  return names.flatMap((n) => (data.states[n]?.polys ?? []).flat());
}
